import { CircleDot, Flag, Play } from 'lucide-react'
import { Card } from '@/components/ui/card'
import type { BusinessStep } from './types'

interface Props {
  steps: BusinessStep[]
}

// First step reads as the trigger, last as the outcome, middles as stages.
function stepIcon(index: number, count: number) {
  if (index === 0) return <Play className="size-3" />
  if (index === count - 1) return <Flag className="size-3" />
  return <CircleDot className="size-3" />
}

// Zapier-style workflow view: each step is its own card with a "Passo N"
// pill, title, and detail, joined by directional connectors. Steps are a
// static definition — no execution status or dates.
export default function BusinessStepsFlow({ steps }: Props) {
  if (steps.length === 0) {
    return <p className="mt-1 text-sm text-muted-foreground">Nenhum passo declarado</p>
  }

  return (
    <div data-testid="business-steps-flow" className="mx-auto flex w-full max-w-md flex-col">
      {steps.map((step, index) => (
        <div key={step.position} className="flex flex-col">
          <Card className="gap-1.5 px-5 py-4 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-primary/25 bg-primary/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
              {stepIcon(index, steps.length)}
              Passo {step.position}
            </span>
            <h3 className="text-[15px] font-semibold leading-snug">{step.title}</h3>
            {step.detail && <p className="text-sm leading-relaxed text-muted-foreground">{step.detail}</p>}
          </Card>

          {index < steps.length - 1 && (
            <div className="flex justify-center py-0.5" aria-hidden="true">
              <svg width="22" height="28" viewBox="0 0 22 28" fill="none" className="text-primary/50">
                <circle cx="11" cy="3" r="2.5" className="fill-primary" />
                <path d="M11 5 V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M6 17 L11 23 L16 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
